import React, { FC } from 'react';

import { SearchInput } from 'src/components/SearchInput';
import { Select } from 'src/components/Select';

import { BarActions, BarButton, DateField, FilterControl, FilterLabel, FilterRow } from './styled';

export type FilterOption = { value: string; label: string };

export type FilterDef = {
  /** The API query parameter this filter sends. */
  key: string;
  label: string;
  placeholder?: string;
  /**
   * Fields the API matches exactly (statuses, providers, blockchains) get a
   * picker: free text for those could only ever return nothing.
   */
  options?: FilterOption[];
  /** Placeholder shown as the "any" choice of a picker. Defaults to "All". */
  allLabel?: string;
  /**
   * A native date input (value is the input's own YYYY-MM-DD). Pages turn it
   * into the ISO instant the API wants, so `to` can be the end of the day.
   */
  type?: 'text' | 'date';
};

export type FilterValues = Record<string, string>;

interface IFilterBar {
  filters: FilterDef[];
  values: FilterValues;
  // eslint-disable-next-line no-unused-vars
  onChange: (key: string, value: string) => void;
  onClear?: () => void;
  /** Re-runs the current query as-is (same page, same filters). */
  onRefresh?: () => void;
  refreshing?: boolean;
}

/**
 * One search bar (or picker) per filter, all active at once — the API ANDs
 * them. Replaces the "pick a field, then type" dropdown on the list pages.
 */
export const FilterBar: FC<IFilterBar> = ({ filters, values, onChange, onClear, onRefresh, refreshing }) => {
  const hasValue = filters.some((filter) => (values[filter.key] ?? '') !== '');

  return (
    <FilterRow>
      {filters.map((filter) => {
        const value = values[filter.key] ?? '';

        return (
          <FilterControl key={filter.key}>
            <FilterLabel>{filter.label}</FilterLabel>
            {filter.type === 'date' ? (
              <DateField
                type="date"
                aria-label={filter.label}
                value={value}
                onChange={(e) => onChange(filter.key, e.currentTarget.value)}
              />
            ) : filter.options ? (
              <Select
                compact
                options={[{ value: '', label: filter.allLabel ?? 'All' }, ...filter.options]}
                value={value}
                onChange={(e) => onChange(filter.key, e.currentTarget.value)}
              />
            ) : (
              <SearchInput
                inline
                showIcon
                placeholder={filter.placeholder ?? `Search ${filter.label.toLowerCase()}...`}
                value={value}
                onChange={(next) => onChange(filter.key, next)}
              />
            )}
          </FilterControl>
        );
      })}
      {onClear || onRefresh ? (
        <BarActions>
          {onClear ? (
            <BarButton type="button" onClick={onClear} disabled={!hasValue} title="Clear every filter">
              Reset
            </BarButton>
          ) : null}
          {onRefresh ? (
            <BarButton type="button" $primary onClick={onRefresh} disabled={refreshing} title="Reload with the current filters">
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </BarButton>
          ) : null}
        </BarActions>
      ) : null}
    </FilterRow>
  );
};

export default FilterBar;
